import React from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'

interface ShortcutRow {
  keys: string
  labelKey: string
  /** Rows that only apply while a cell editor is already open — the same key combo (Tab/Shift+Tab,
   * Enter) means something different there than it does on a focused, non-editing row. */
  whileEditing?: boolean
}

interface ShortcutGroup {
  titleKey: string
  rows: ShortcutRow[]
}

/** Task 3 (Freelo #32253610): the keyboard map documented here must stay in sync with
 * `hooks/useGanttKeyboard.ts` / `hooks/useCellEditing.ts` / `detail/cells/CellEditor.tsx`. */
const GROUPS: ShortcutGroup[] = [
  {
    titleKey: 'Schedule_shortcuts_nav',
    rows: [
      { keys: '↑ ↓', labelKey: 'Schedule_sc_move' },
      { keys: 'Shift+↑ ↓', labelKey: 'Schedule_sc_extend' },
      { keys: 'Home / End', labelKey: 'Schedule_sc_home_end' },
      { keys: '←', labelKey: 'Schedule_sc_collapse' },
      { keys: '→', labelKey: 'Schedule_sc_expand' },
    ],
  },
  {
    titleKey: 'Schedule_shortcuts_edit',
    rows: [
      { keys: 'Enter / F2', labelKey: 'Schedule_sc_edit_name' },
      { keys: 'Tab / Shift+Tab', labelKey: 'Schedule_sc_tab_row' },
      { keys: 'Tab / Shift+Tab', labelKey: 'Schedule_sc_tab_cell', whileEditing: true },
      { keys: 'Enter', labelKey: 'Schedule_sc_enter_commit', whileEditing: true },
      { keys: 'Shift+Enter / Insert', labelKey: 'Schedule_sc_shift_enter', whileEditing: true },
      { keys: 'Esc', labelKey: 'Schedule_sc_escape' },
      { keys: 'Pred.', labelKey: 'Schedule_sc_predecessors' },
    ],
  },
  {
    titleKey: 'Schedule_shortcuts_structure',
    rows: [
      { keys: 'Insert / Shift+Enter', labelKey: 'Schedule_sc_insert' },
      { keys: 'Delete', labelKey: 'Schedule_sc_delete' },
      { keys: 'Alt+Shift+→ / ←', labelKey: 'Schedule_sc_indent_outdent' },
      { keys: 'Alt+Shift+↑ / ↓', labelKey: 'Schedule_sc_move_up_down' },
    ],
  },
  {
    titleKey: 'Schedule_shortcuts_view',
    rows: [
      { keys: '+ / −', labelKey: 'Schedule_sc_zoom' },
      { keys: '?', labelKey: 'Schedule_sc_help' },
    ],
  },
]

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const ShortcutsDialog: React.FC<Props> = ({ open, onOpenChange }) => {
  const { t } = useTranslation()
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('Schedule_shortcuts')}</DialogTitle>
          {/* Radix warns when a DialogContent has no description; keep it for screen readers only. */}
          <DialogDescription className="sr-only">{t('Schedule_shortcuts_description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {GROUPS.map((group) => (
            <div key={group.titleKey}>
              <h3 className="text-sm font-semibold text-foreground mb-1.5">{t(group.titleKey)}</h3>
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 items-baseline">
                {group.rows.map((row, i) => (
                  <React.Fragment key={`${group.titleKey}-${i}`}>
                    <dt className="flex items-center gap-1.5 flex-wrap">
                      <kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-xs">{row.keys}</kbd>
                      {row.whileEditing && <span className="text-xs text-muted-foreground">{t('Schedule_sc_while_editing')}</span>}
                    </dt>
                    <dd className="text-sm text-muted-foreground">{t(row.labelKey)}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}
export default ShortcutsDialog
